/* eslint-disable max-lines */
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {AvatarAction, AvatarActionInput, Challenge} from "../../../schema";
import {gql} from "@apollo/client";
import {limitSpeechTextOnChange} from "./ActionEditForm";

import Button from "../../../ui/button";
import {useFormState} from "../../../hooks/useFormState";
import AvatarEmotionSelect from "./AvatarEmotionSelect";
import AvatarGestureSelect from "./AvatarGestureSelect";
import {AVATAR_PHRASE_MAX_LENGTH} from "../../../settings";
import {AlertCircleIcon, HelpCircleIcon} from "../../../ui/icons";
import WithTooltip from "../../../ui/tooltip";
import {parseMimeType} from "../../../utils";
import Checkbox from "../../../ui/checkbox";
import FileInput from "../FileInput";
import LimitedTextarea from "../LimitedTextarea";

import cn from "classnames";
import classes from "./forms.module.css";
import {useCurrentUser} from "../../../App.context";

type props = {
  action: AvatarAction;
  noAvatar?: boolean;
  phraseLimit?: number;
  onSubmit?: (data: AvatarActionInput) => void;
  testId?: string;
  challenge?: Challenge;
};

type formState = {
  text: NonNullable<AvatarActionInput["text"]>;
  customAudio?: AvatarActionInput["customAudio"];
  emotion: AvatarActionInput["emotion"];
  gesture: AvatarActionInput["gesture"];
  media?: File | "";
  mediaViewRequired?: NonNullable<AvatarActionInput["mediaViewRequired"]>;
  setMediaAsBackground?: NonNullable<AvatarActionInput["setMediaAsBackground"]>;
  replaceCharacterWithMedia?: NonNullable<AvatarActionInput["replaceCharacterWithMedia"]>;
};

export default function AvatarActionEditForm({action, noAvatar, phraseLimit, onSubmit, testId, challenge}: props) {
  const {t} = useTranslation();
  const inExperimentalMode = useCurrentUser()?.experimentalMode;

  const formState = useFormState<formState>({
    initialValues: {
      text: action.text ?? "",
      emotion: action.emotion,
      gesture: action.gesture,
      mediaViewRequired: action.mediaViewRequired ?? undefined,
      setMediaAsBackground: action.setMediaAsBackground ?? undefined,
      replaceCharacterWithMedia: action.replaceCharacterWithMedia ?? undefined,
    },
    preventDefault: true,
    onSubmit: (data) => {
      onSubmit &&
        onSubmit({
          ...data,
          customAudio: data.customAudio === "" ? null : data.customAudio,
          media: data.media === "" ? null : data.media,
          mediaViewRequired: !!data.mediaViewRequired || mediaIsVideo,
          setMediaAsBackground: !!data.setMediaAsBackground,
          replaceCharacterWithMedia: !!data.replaceCharacterWithMedia,
        });
    },
  });

  const audioSrc = useMemo<string | undefined>(() => {
    if (!!formState.values.customAudio || (action.isCustomAudio && action.audio)) {
      if (formState.values.customAudio !== "") {
        return formState.values.customAudio ?? action.audio;
      }
    }
  }, [action.audio, action.isCustomAudio, formState.values.customAudio]);

  const [mediaSrc, currentMediaType]: [string | undefined | File, string | undefined] = useMemo(() => {
    if (!!formState.values.media || action.media) {
      if (formState.values.media !== "") {
        return [
          formState.values.media ?? action.media ?? undefined,
          formState.values.media?.type ?? action.mediaType ?? undefined,
        ];
      }
    }
    return [undefined, undefined];
  }, [action.media, action.mediaType, formState.values.media]);

  const mediaIsVideo = !!currentMediaType && parseMimeType(currentMediaType).type === "video";

  const handleExclusiveCheckboxChange = (
    name: "setMediaAsBackground" | "replaceCharacterWithMedia",
    checked: boolean,
  ) => {
    formState.setValues((prev) => ({
      ...prev,
      setMediaAsBackground: name === "setMediaAsBackground" ? (checked ? true : undefined) : undefined,
      replaceCharacterWithMedia: name === "replaceCharacterWithMedia" ? (checked ? true : undefined) : undefined,
    }));
  };

  return (
    <form data-testid={`AvatarActionEditForm:${testId}`} method="post" onSubmit={formState.submitHandler}>
      <div className={classes.row}>
        <label className={classes.label} htmlFor="text">
          {t("components.ActionEditForm.avatarSpeakText.title")}{" "}
          <WithTooltip
            className={classes.help}
            as="span"
            helpText={t("components.ActionEditForm.avatarSpeakText.help")}
          >
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <LimitedTextarea
          maxLength={phraseLimit ?? AVATAR_PHRASE_MAX_LENGTH}
          id="text"
          name="text"
          rows={4}
          value={formState.values.text!}
          onChange={formState.changeHandler}
          limitTextOnChange={limitSpeechTextOnChange}
          placeholder={t("components.ActionEditForm.avatarSpeakText.placeholder")}
        />
      </div>
      <div className={classes.row}>
        <label className={classes.label}>
          {t("components.ActionEditForm.userMediafile.title")}{" "}
          <WithTooltip className={classes.help} as="span" helpText={t("components.ActionEditForm.userMediafile.help")}>
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <FileInput
          variant="audio"
          src={audioSrc ?? undefined}
          name="customAudio"
          allowMicInput
          onDelete={formState.changeHandler}
          setValues={formState.setValues}
        />
      </div>

      {!noAvatar && (
        <div className={classes.row}>
          <label className={classes.label}>
            {t("components.ActionEditForm.avatarEmotion.title")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ActionEditForm.avatarEmotion.help")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <AvatarEmotionSelect name="emotion" value={formState.values.emotion!} onChange={formState.changeHandler} />
        </div>
      )}

      {!noAvatar && (
        <div className={classes.row}>
          <label className={classes.label}>
            {t("components.ActionEditForm.avatarGesture.title")}{" "}
            <WithTooltip
              className={classes.help}
              as="span"
              helpText={t("components.ActionEditForm.avatarGesture.help")}
            >
              <HelpCircleIcon />
            </WithTooltip>
          </label>
          <AvatarGestureSelect name="gesture" value={formState.values.gesture!} onChange={formState.changeHandler} />
        </div>
      )}

      <div className={cn(classes.row, classes.mediaPreviewRow)}>
        <label className={classes.label} htmlFor="text">
          {t("components.ActionEditForm.media.title")}{" "}
          <WithTooltip className={classes.help} as="span" helpText={t("components.ActionEditForm.media.help")}>
            <HelpCircleIcon />
          </WithTooltip>
        </label>
        <FileInput
          className={classes.mediaPreview}
          variant={["image", "video"]}
          src={mediaSrc}
          name="media"
          onDelete={formState.changeHandler}
          setValues={formState.setValues}
        />
      </div>

      {mediaSrc && (
        <div className={classes.row}>
          <label>
            <Checkbox
              checked={!!formState.values.mediaViewRequired || mediaIsVideo}
              name="mediaViewRequired"
              disabled={mediaIsVideo}
              onChange={formState.changeHandler}
            />
            <span className={classes.checkboxText}>
              {t("components.ActionEditForm.mediaViewRequired.title")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ActionEditForm.mediaViewRequired.help")}
              >
                <HelpCircleIcon />
              </WithTooltip>
            </span>
          </label>
        </div>
      )}

      {inExperimentalMode && mediaSrc && (
        <div className={classes.row}>
          <label className={classes.checkboxBlock}>
            <Checkbox
              checked={!!formState.values.setMediaAsBackground}
              disabled={mediaIsVideo}
              name="setMediaAsBackground"
              onChange={(e) => handleExclusiveCheckboxChange("setMediaAsBackground", e.target.checked)}
            />
            <span className={cn(classes.checkboxText, classes.checkboxBlock)}>
              {t("components.ActionEditForm.setMediaAsBackground.title")}{" "}
              <WithTooltip
                className={classes.help}
                as="span"
                helpText={t("components.ActionEditForm.setMediaAsBackground.help")}
              >
                <HelpCircleIcon />
              </WithTooltip>{" "}
              {mediaIsVideo && (
                <WithTooltip helpText={t("components.ActionEditForm.setMediaAsBackground.warning")} as="span">
                  <AlertCircleIcon className={classes.warning} />
                </WithTooltip>
              )}
            </span>
          </label>
        </div>
      )}

      {inExperimentalMode && mediaSrc && (
        <div className={classes.row}>
          <label>
            <Checkbox
              checked={!!formState.values.replaceCharacterWithMedia}
              name="replaceCharacterWithMedia"
              onChange={e => handleExclusiveCheckboxChange("replaceCharacterWithMedia", e.target.checked)}
            />
            <span className={classes.checkboxText}>
              {t("components.ActionEditForm.replaceCharacterWithMedia.title")}
              {" "}
              <WithTooltip className={classes.help} as='span'
                           helpText={t("components.ActionEditForm.replaceCharacterWithMedia.help")}>
                <HelpCircleIcon/>
              </WithTooltip>
            </span>
          </label>
        </div>
      )}

      <div className={classes.row}>
        <Button color="danger">{t("common.save")}</Button>
      </div>
    </form>
  );
}

AvatarActionEditForm.fragments = {
  root: gql`
    fragment AvatarActionEditForm on AvatarAction {
      id
      text
      isCustomAudio
      emotion
      gesture
      media
      mediaType
      mediaViewRequired
      setMediaAsBackground
      replaceCharacterWithMedia
    }
  `,
};
